import React from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface SecretFieldProps {
  id: string;
  label: string;
  value: string;
  cleared: boolean;
  isFirstSetup: boolean;
  canClear: boolean;
  placeholderCreate?: string;
  error?: string;
  onChange: (next: string) => void;
  onToggleClear: (next: boolean) => void;
}

export const SecretField: React.FC<SecretFieldProps> = ({
  id,
  label,
  value,
  cleared,
  isFirstSetup,
  canClear,
  placeholderCreate,
  error,
  onChange,
  onToggleClear,
}) => {
  const { t } = useTranslation();
  const [reveal, setReveal] = React.useState(false);

  const placeholder = isFirstSetup
    ? (placeholderCreate ?? "")
    : t("communicationChannels.secret.placeholderEdit");

  const clearDisabled = !canClear || value.length > 0;
  const clearTooltip = canClear
    ? undefined
    : t("communicationChannels.secret.clearDisabledTooltip");

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>
        {label}
        {isFirstSetup && " *"}
      </Label>
      <div className="flex items-start gap-2">
        <div className="relative flex-1">
          <Input
            id={id}
            type={reveal ? "text" : "password"}
            autoComplete="off"
            placeholder={placeholder}
            value={value}
            disabled={cleared}
            className="pr-10"
            onChange={(event) => onChange(event.target.value)}
          />
          <button
            type="button"
            onClick={() => setReveal((prev) => !prev)}
            aria-label={
              reveal
                ? t("communicationChannels.secret.hide")
                : t("communicationChannels.secret.show")
            }
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {reveal ? (
              <EyeOff className="size-4" />
            ) : (
              <Eye className="size-4" />
            )}
          </button>
        </div>
        {!isFirstSetup && (
          <Button
            type="button"
            variant="new_outline"
            size="sm"
            disabled={clearDisabled && !cleared}
            onClick={() => onToggleClear(!cleared)}
            title={clearTooltip}
          >
            {cleared
              ? t("communicationChannels.secret.clearArmed")
              : t("communicationChannels.secret.clear")}
          </Button>
        )}
      </div>
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : (
        !isFirstSetup && (
          <p className="text-xs text-muted-foreground">
            {t("communicationChannels.secret.helpEdit")}
          </p>
        )
      )}
    </div>
  );
};
